import { IconPartner, IconSmallPartner } from '@hivebrite/design-system/Icon';
import { convertRemToPx } from '@hivebrite/design-system/Theme';

import { getItemArguments } from 'hb-builder/components/BuilderComponents/page/block/Partners/utils/getItemArguments';
import { instantiateLogos } from 'hb-builder/components/BuilderComponents/page/block/Partners/utils/instantiateLogos';
import type { RenderProps } from 'hb-builder/components/common/type/RenderProps';
import { defaultTabs } from 'hb-builder/constants/tabs';
import {
  useComponentContext,
  useComponentsContext,
  useRendererContext,
} from 'hb-builder/hooks';
import type { BuilderComponent } from 'hb-builder/types';
import type { DocumentItem } from 'hb-react/backoffice/types/builder/builderDocument';
import type { PartnersItemAttributes } from 'hb-react/backoffice/types/builder/itemAttributes/partnersItemAttributes';

const Partners: BuilderComponent<PartnersItemAttributes> = () => <div />; // never used

const RenderBuilder = ({ children }) => {
  const { item } = useComponentContext();
  const { components } = useComponentsContext();

  const { attributes } = item;
  const { Section } = components;

  return (
    <Section.RenderBuilder attributes={attributes}>
      {children}
    </Section.RenderBuilder>
  );
};

const Render = ({
  children,
  item = { id: '', type: '' },
}: RenderProps<DocumentItem<PartnersItemAttributes>>) => {
  const { bodyWidth, isMobilePreview, workspaceConfig } = useRendererContext();
  const { components } = useComponentsContext();
  const { Section } = components;

  return (
    <Section.Render
      bodyWidth={bodyWidth}
      components={components}
      isMobilePreview={isMobilePreview}
      item={item}
      workspaceConfig={workspaceConfig}
    >
      {children}
    </Section.Render>
  );
};

Partners.componentCategory = 'block';

Partners.componentFamily = 'Logos';
Partners.componentLabel = __('Logos');
Partners.componentName = 'PartnersTemplateFour';
Partners.componentType = 'section';

Partners.canBeDragged = true;
Partners.canBeListed = true;
Partners.canBeSelected = true;
Partners.componentClickable = false;
Partners.canBeDoubleClicked = true;

Partners.item = ({ bodyWidth, components = {}, features = {}, theme }) => {
  const { Column, Partner, PartnersShifted, Section, Text } = components;

  const headline = Text.item({
    features: {
      ...features,
      text: {
        ...features?.text,
        containerPadding: '0px 50px 0px 10px',
        content: __('Meet our great partners'),
        defaultTextColor: true,
        horizontalAlign: 'center',
        hidden: false,
        textColor: '#000',
        textFontSize: theme
          ? convertRemToPx(parseFloat(theme.fontSizes[9]))
          : undefined,
        tied: true,
      },
    },
    theme,
  });

  const partnersShiftedItemArguments =
    features &&
    getItemArguments({
      features,
      component: Partner,
      template: 'PartnersTemplateFour',
    });

  const partnersShifted = PartnersShifted.item({
    children: partnersShiftedItemArguments
      ? instantiateLogos({
          components,
          ...partnersShiftedItemArguments,
        })
      : undefined,
  });

  return Section.item({
    children: [
      Column.item({
        children: [headline],
        disableSelection: true,
        width: '40%',
      }),

      Column.item({
        children: [partnersShifted],
        disableSelection: true,
        width: '60%',
      }),
    ],
    components,
    features: {
      ...features,
      section: {
        ...features.section,
        name: Partners.componentName,
        type: Partners.componentName,
        containerBackgroundColor: '#EBEEF4',
        containerPadding: '72px 0px 72px 0px',
        fullWidth: true,
        keepContainerPaddingScreenS: true,
        sectionWidth: bodyWidth,
      },
    },
  });
};

Partners.componentIcon = ({ config } = {}) =>
  config?.features?.panel?.applyNewDesign ? IconSmallPartner : IconPartner;

Partners.toolbar = ['edit', 'drag', 'duplicate', 'delete'];

Partners.SettingsTabs = defaultTabs;

Partners.RenderBuilder = RenderBuilder;
Partners.Render = Render;

export default Partners;
